import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Download, FileText, Pencil, Pin, Palette } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { GetDocumentById } from "../apis/documents";
import { DocumentRecord } from "./documentTypes";
import { richTextToPlainText, sanitizeDocumentHtml } from "./richText";
import { useContentSearch } from "./useContentSearch";
import SearchBar from "./SearchBar";

// ─────────────────────────────────────────────────────────────
// THEME PALETTE — change colors here whenever needed
// Each theme: bg = content area background, text = body text color
// ─────────────────────────────────────────────────────────────
const THEMES = {
  "Off-White":   { bg: "#fafaf8", text: "#1e1e1e", link: "#1d4ed8", label: "Off-White",   dot: "#fafaf8", border: "#e2e2dc" },
  "Warm Yellow": { bg: "#fef9e7", text: "#2d2000", link: "#b45309", label: "Warm Yellow", dot: "#fef3c7", border: "#fde68a" },
  "Soft Green":  { bg: "#f0fdf4", text: "#052e16", link: "#15803d", label: "Soft Green",  dot: "#bbf7d0", border: "#86efac" },
  "Light Gray":  { bg: "#E1E1E1", text: "#333333", link: "#4183C4", label: "Light Gray",  dot: "#E1E1E1", border: "#c8c8c8" },
  "Charcoal":    { bg: "#292C2F", text: "#D5D5D5", link: "#2B6EA1", label: "Charcoal",    dot: "#292C2F", border: "#3f4347" },
  "Deep Navy":   { bg: "#161923", text: "#D5D5D5", link: "#39ADBD", label: "Deep Navy",   dot: "#161923", border: "#1e2535" },
  "Midnight":    { bg: "#0F1419", text: "#E6E1CF", link: "#0096CF", label: "Midnight",    dot: "#0F1419", border: "#1a2030" },
  "Black":       { bg: "#0a0a0a", text: "#f5f5f5", link: "#60a5fa", label: "Black",       dot: "#171717", border: "#262626" },
} as const;

type ThemeKey = keyof typeof THEMES;
const THEME_KEYS = Object.keys(THEMES) as ThemeKey[];
const DEFAULT_THEME: ThemeKey = "Off-White";

export default function DocumentDetails() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const [document, setDocument] = useState<DocumentRecord | null>(null);
  const [loading, setLoading]   = useState(true);
  const [theme, setTheme]       = useState<ThemeKey>(DEFAULT_THEME);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const contentDivRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef   = useRef<HTMLDivElement | null>(null);
  const search        = useContentSearch();

  const captureContentRef = (el: HTMLDivElement | null) => {
    contentDivRef.current    = el;
    search.contentRef.current = el;
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.document.addEventListener("mousedown", handler);
    return () => window.document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await GetDocumentById(id);
        setDocument(response?.data || null);
      } catch (error) {
        console.error(error);
        toast.error("Failed to load document");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="mt-8 h-80 animate-pulse rounded-lg border border-slate-800 bg-slate-900/70" />;
  if (!document) return <div className="mt-8 rounded-lg border border-slate-800 bg-slate-900/70 p-6 text-slate-300">Document not found.</div>;

  const activeTheme = THEMES[theme];

  return (
    <div className="mt-8 space-y-5 pb-10">

      {/* ── Top bar ── */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate("/documents")}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex gap-2">
          {document.file_url && (
            <button
              onClick={() => window.open(document.file_url || "", "_blank", "noopener,noreferrer")}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 px-3 py-2 text-sm text-slate-200 hover:bg-slate-800"
            >
              <Download size={16} /> Download
            </button>
          )}
          <button
            onClick={() => navigate(`/documents/edit/${document.id}`)}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-3 py-2 text-sm font-semibold text-white hover:bg-blue-500"
          >
            <Pencil size={16} /> Edit
          </button>
        </div>
      </div>

      {/* ── Meta section ── */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-5">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-500/10 text-blue-300">
            <FileText />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-black text-white">{document.title}</h1>
              {document.is_pinned && <Pin size={16} className="text-amber-300" />}
            </div>
            <p className="mt-1 text-slate-400">{document.subtitle}</p>
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 text-sm md:grid-cols-3">
          <div className="rounded-lg bg-slate-950 p-3 text-slate-300">Category: {document.category || "N/A"}</div>
          <div className="rounded-lg bg-slate-950 p-3 text-slate-300">Uploaded by: {document.uploaded_by || "N/A"}</div>
          <div className="rounded-lg bg-slate-950 p-3 text-slate-300">Views: {document.view_count || 0}</div>
        </div>

        <p className="mt-5 whitespace-pre-wrap text-sm leading-6 text-slate-200">
          {document.description || "No description."}
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {(document.tags || []).map((tag) => (
            <span key={tag} className="rounded-full border border-slate-700 px-2 py-1 text-xs text-slate-300">
              {tag}
            </span>
          ))}
        </div>
      </section>

      {/* ── Content section ── */}
      <section className="rounded-lg border border-slate-800 bg-slate-900/70 p-5">

        {/* Section header: title + theme picker + search */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-bold text-white">Content</h2>

          <div className="flex flex-wrap items-center gap-2">

            {/* ── Theme dropdown ── */}
            {document.content_text && (
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => setDropdownOpen((o) => !o)}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-900 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800"
                >
                  {/* Active colour dot */}
                  <span
                    className="h-3 w-3 rounded-full border"
                    style={{ backgroundColor: activeTheme.dot, borderColor: activeTheme.border }}
                  />
                  <Palette size={13} />
                  <span>{activeTheme.label}</span>
                  <span className="text-slate-500">▾</span>
                </button>

                {dropdownOpen && (
                  <div className="absolute right-0 top-full z-50 mt-1 w-44 overflow-hidden rounded-xl border border-slate-700 bg-slate-900 shadow-2xl shadow-black/40">
                    {THEME_KEYS.map((key) => {
                      const t = THEMES[key];
                      const isActive = key === theme;
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => { setTheme(key); setDropdownOpen(false); }}
                          className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                            isActive
                              ? "bg-blue-600/20 text-blue-300"
                              : "text-slate-200 hover:bg-slate-800"
                          }`}
                        >
                          {/* Colour swatch */}
                          <span
                            className="h-4 w-4 flex-shrink-0 rounded-full border"
                            style={{ backgroundColor: t.bg, borderColor: t.border }}
                          />
                          {t.label}
                          {isActive && <span className="ml-auto text-blue-400">✓</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* ── Search bar ── */}
            {document.content_text && (
              <SearchBar
                searchQuery={search.searchQuery}
                setSearchQuery={search.setSearchQuery}
                matchCount={search.matchCount}
                currentMatch={search.currentMatch}
                goNext={search.goNext}
                goPrev={search.goPrev}
                clearSearch={search.clearSearch}
              />
            )}
          </div>
        </div>

        {/* ── Rendered content with active theme applied ── */}
        {document.content_text ? (
          <>
            {/* Scoped link color via CSS variable */}
            <style>{`
              .doc-content a { color: var(--link-color); }
              .doc-content a:hover { opacity: 0.8; }
            `}</style>
            <div
              ref={captureContentRef}
              className="doc-content mt-1 rounded-lg px-6 py-5 text-[15px] leading-7 transition-colors duration-300 [&_a]:underline [&_h1]:mb-3 [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:text-xl [&_h3]:font-bold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:list-disc"
              style={{
                backgroundColor: activeTheme.bg,
                color: activeTheme.text,
                "--link-color": activeTheme.link,
              } as React.CSSProperties}
              dangerouslySetInnerHTML={{ __html: sanitizeDocumentHtml(document.content_text) }}
            />
          </>
        ) : (
          <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-300">
            {richTextToPlainText(document.extracted_text || "") || "No searchable content stored for this document."}
          </p>
        )}
      </section>
    </div>
  );
}
